/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { classNames } from '../utils/UNSAFE_classNames';
import { useTooltip } from '../hooks/UNSAFE_useTooltip';
import { Text } from '../UNSAFE_Text';
import { timeAxisLabelsStyles } from './themes/TimeComponentStyles.css';

type Props = {
  x: number;
  y: number;
  width: number;
  label: string;
  labelPosition: 'start' | 'center';
  font: { fontSize?: string; fontWeight?: string };
  isTruncated: boolean;
  isStartIntervalTruncated: boolean;
  isRTL: boolean;
};

export function TimeComponentHorizontalTimeAxisLabel({
  x,
  y,
  width,
  label,
  font,
  labelPosition,
  isTruncated,
  isStartIntervalTruncated,
  isRTL
}: Props) {
  const {
    tooltipContent,
    tooltipProps: { onFocus, onBlur, ...restTooltipProps }
  } = useTooltip({ text: label, isDisabled: !isTruncated });

  const textAlign = labelPosition === 'center' ? 'center' : undefined;

  // If start truncated, we want the ellipsis to be the prefix rather than suffix in ltr,
  // so a trick is to locally set the direction to rtl
  const direction = isStartIntervalTruncated && isTruncated ? (isRTL ? 'ltr' : 'rtl') : undefined;

  return (
    <div
      {...restTooltipProps}
      aria-hidden="true"
      class={classNames([
        timeAxisLabelsStyles.label,
        isTruncated && timeAxisLabelsStyles.labelTruncated
      ])}
      style={{ left: x, top: y, width, textAlign, direction, ...font }}>
      <Text variant="primary" truncation="ellipsis" size="inherit" weight="inherit">
        {label}
      </Text>
      {tooltipContent}
    </div>
  );
}
